import React, { useEffect, useRef, useState } from 'react'
import { httpsCallable } from 'firebase/functions'
import { functions } from '../../helpers/firebase'
import {
  acceptedBillingUrl,
  billingNavigation,
  tierAccessLabel,
  tierModalBody
} from './parentBillingDisplay'
import './ParentTierModal.css'

const PAYMENT_ERROR = 'Could not open payment. Try again.'

const ParentTierModal = ({ summary, displayName, onClose }) => {
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)
  const live = useRef(true)
  const inFlight = useRef(false)
  const primaryRef = useRef(null)

  useEffect(() => {
    live.current = true
    const node = primaryRef.current
    if (node && node.focus) node.focus()
    return () => {
      live.current = false
    }
  }, [])

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  if (!summary || !tierAccessLabel(summary.access)) return null

  const body = tierModalBody(summary)
  const name = (displayName || '').trim() || 'Student'
  const paid = summary.access === 'paid'

  const redirect = async (callableName, payload) => {
    if (inFlight.current) return
    inFlight.current = true
    setBusy(true)
    setFailed(false)
    try {
      if (callableName === 'createStudentUpgradeCheckout') {
        const studentId = summary.studentId
        if (typeof studentId !== 'string' || !studentId.trim()) {
          throw new Error('missing student')
        }
      }
      const call = httpsCallable(functions, callableName)
      const result = await call(payload)
      const url = acceptedBillingUrl(result && result.data && result.data.url)
      if (!url) throw new Error('missing url')
      // Closing the modal, including Stay on Free, unmounts us first.
      // A late response must not navigate after that.
      if (!live.current) return
      billingNavigation.assign(url)
    } catch (error) {
      const code = error && typeof error.code === 'string' ? error.code : ''
      console.error('Billing redirect failed', code)
      if (!live.current) return
      setFailed(true)
      setBusy(false)
      inFlight.current = false
    }
  }

  const startUpgrade = () => {
    redirect('createStudentUpgradeCheckout', { studentId: summary.studentId })
  }

  const startPortal = () => {
    redirect('createBillingPortalSession', {})
  }

  return (
    <div
      className="modal-overlay parent-tier-modal-overlay"
      onClick={onClose}
    >
      <div
        className="parent-code-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="parent-tier-modal-title"
        aria-describedby={body ? 'parent-tier-modal-body' : undefined}
        onClick={(event) => event.stopPropagation()}
      >
        <h2 id="parent-tier-modal-title">Unlimited</h2>
        <p className="parent-tier-modal-name">{name}</p>
        {body && <p id="parent-tier-modal-body">{body}</p>}
        {failed && (
          <p className="parent-tier-modal-error" role="alert">
            {PAYMENT_ERROR}
          </p>
        )}
        <div className="modal-actions">
          {summary.access === 'free' && (
            <>
              <button
                type="button"
                className="save-button"
                ref={primaryRef}
                onClick={startUpgrade}
                disabled={busy}
              >
                Upgrade
              </button>
              <button type="button" className="close-button" onClick={onClose}>
                Stay on Free
              </button>
            </>
          )}
          {paid && (
            <button
              type="button"
              className="save-button"
              ref={primaryRef}
              onClick={startPortal}
              disabled={busy}
            >
              Manage
            </button>
          )}
          {summary.access === 'complimentary' && (
            <>
              <button
                type="button"
                className="save-button"
                ref={primaryRef}
                onClick={startUpgrade}
                disabled={busy}
              >
                Keep Unlimited
              </button>
              <button type="button" className="close-button" onClick={onClose}>
                Close
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default ParentTierModal
